// @flow strict
import * as React from 'react';
import * as Immutable from 'immutable';
import { useEffect, useState } from 'react';
import styled, { type StyledComponent } from 'styled-components';

import { TextOverflowEllipsis, DataTable, Spinner, PaginatedList, SearchForm, EmptyResult } from 'components/common';
import { Link } from 'components/graylog/router';
import Routes from 'routing/Routes';
import { Col, Row } from 'components/graylog';
import { type ThemeInterface } from 'theme';
import RolesCell from 'components/permissions/RolesCell';

import ActionsCell from './ActionsCell';
import UsersCell from './UsersCell';
import TeamsQueryHelp from './TeamsQueryHelper';
import SourceTypeCell from './SourceTypeCell';

import LicenseCheck from '../../../license/LicenseCheck';
import type { PaginatedTeams } from '../../actions/TeamsActions';
import TeamsDomain from '../../domainActions/TeamsDomain';
import { TeamsActions } from '../../stores/TeamsStore';

const TABLE_HEADERS = ['Name', 'Description', 'Source', 'Users', 'Roles', 'Actions'];
const DEFAULT_PAGINATION = { page: 1, perPage: 10, query: '' };

const Container: StyledComponent<{}, ThemeInterface, HTMLDivElement> = styled.div`
  .data-table {
    overflow-x: visible;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
`;

const LoadingSpinner = styled(Spinner)(({ theme }) => `
  margin-left: 10px;
  font-size: ${theme.fonts.size.h3};
`);

const DescriptionCell = styled.td`
  max-width: 300px;
`;

const _teamFormatter = (team, rolesContext, usersContext, readOnly) => {
  const { roles: teamRolesIds = Immutable.Set() } = team;
  const teamRolesNames = teamRolesIds.map((roleId) => {
    return rolesContext.get(roleId)?.title ?? `${roleId} (Name not found)`;
  });

  return (
    <tr key={team.id}>
      <td className="limited"><Link to={Routes.pluginRoute('SYSTEM_TEAMS_TEAMID')(team.id)}>{team.name}</Link></td>
      <DescriptionCell>
        <TextOverflowEllipsis>
          {team.description}
        </TextOverflowEllipsis>
      </DescriptionCell>
      <SourceTypeCell team={team} />
      <UsersCell team={team} usersContext={usersContext} rolesContext={rolesContext} />
      <RolesCell roles={teamRolesNames} />
      <ActionsCell team={team} readOnly={readOnly} />
    </tr>
  );
};

const _headerCellFormatter = (header) => {
  switch (header.toLocaleLowerCase()) {
    case 'actions':
      return <th className="actions text-right">{header}</th>;
    default:
      return <th>{header}</th>;
  }
};

const _loadTeams = (pagination, setLoading, setPaginatedTeams) => {
  setLoading(true);

  TeamsDomain.loadTeamsPaginated(pagination).then((paginatedTeams) => {
    setPaginatedTeams(paginatedTeams);
    setLoading(false);
  });
};

const _updateListOnTeamDelete = (perPage, query, setPagination) => TeamsActions.delete.completed.listen(() => setPagination({ page: DEFAULT_PAGINATION.page, perPage, query }));

const TeamsOverview = () => {
  const [paginatedTeams, setPaginatedTeams] = useState<?PaginatedTeams>();
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [loading, setLoading] = useState(false);
  const { page, query, perPage } = pagination;

  useEffect(() => _loadTeams(pagination, setLoading, setPaginatedTeams), [pagination]);
  useEffect(() => _updateListOnTeamDelete(perPage, query, setPagination), [perPage, query]);

  const _handleSearch = (newQuery) => setPagination({ ...pagination, query: newQuery, page: DEFAULT_PAGINATION.page });

  const searchFilter = (
    <SearchForm onSearch={_handleSearch}
                onReset={() => _handleSearch('')}
                wrapperClass="has-bm"
                queryHelpComponent={<TeamsQueryHelp />}
                topMargin={0} />
  );

  if (!paginatedTeams) {
    return <Spinner />;
  }

  return (
    <LicenseCheck displayWarningContainer>
      {({ licenseIsValid }) => (
        <Container>
          <Row className="content">
            <Col xs={12}>
              <Header>
                <h2>Teams</h2>
                {loading && <LoadingSpinner text="" delay={0} />}
              </Header>
              <p className="description">
                Found {paginatedTeams.pagination.total} teams on the system.
              </p>
              <PaginatedList activePage={page}
                             totalItems={paginatedTeams.pagination.total}
                             onChange={(newPage, newPerPage) => setPagination({
                               ...pagination,
                               page: newPage,
                               perPage: newPerPage,
                             })}>
                <DataTable id="teams-overview"
                           className="table-hover"
                           headers={TABLE_HEADERS}
                           rowClassName="no-bm"
                           sortByKey="name"
                           rows={paginatedTeams.list.toJS()}
                           noDataText={<EmptyResult>No teams have been found.</EmptyResult>}
                           headerCellFormatter={_headerCellFormatter}
                           dataRowFormatter={(team) => _teamFormatter(team, paginatedTeams.context.roles, paginatedTeams.context.users, !licenseIsValid)}
                           customFilter={searchFilter}
                           filterKeys={[]}
                           filterLabel="Filter Teams" />
              </PaginatedList>
            </Col>
          </Row>
        </Container>
      )}
    </LicenseCheck>
  );
};

export default TeamsOverview;
